import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { config } from '../constants.js';

const url = config.url.API_URL;

const ANONYMOUS_ACCESS = {
  tier: 'anonymous',
  canAccessAll: false,
  canJoinClass: false,
  canUseRememberMe: false,
  trialDaysRemaining: null,
  accessDaysRemaining: null,
  showJoinModalOnComplete: false,
  showRenewInNavbar: false,
  isGrandfathered: false,
};

const PaywallContext = createContext(null);

/**
 * @param {object} props
 * @param {boolean} props.authChecked
 * @param {boolean} props.isAuthenticated
 * @param {object|null} [props.initialAccess]
 * @param {React.ReactNode} props.children
 */
export function PaywallProvider({ authChecked, isAuthenticated, initialAccess = null, children }) {
  const [access, setAccess] = useState(() => {
    if (!isAuthenticated) {
      return ANONYMOUS_ACCESS;
    }
    return initialAccess ?? ANONYMOUS_ACCESS;
  });
  const [accessLoading, setAccessLoading] = useState(() => {
    if (!authChecked) {
      return true;
    }
    if (!isAuthenticated) {
      return false;
    }
    return !initialAccess;
  });
  const [signInModalOpen, setSignInModalOpen] = useState(false);
  const [signInModalContext, setSignInModalContext] = useState(null);
  const [joinModalOpen, setJoinModalOpen] = useState(false);

  const refreshAccess = useCallback(async () => {
    if (!authChecked) {
      return;
    }

    setAccessLoading(true);
    try {
      if (!isAuthenticated) {
        setAccess(ANONYMOUS_ACCESS);
        return;
      }

      const response = await fetch(`${url}/record/checkAuth`, {
        method: 'GET',
        credentials: 'include',
      });
      if (!response.ok) {
        throw new Error(`checkAuth failed: ${response.status}`);
      }
      const data = await response.json();
      if (data.authenticated && data.access) {
        setAccess(data.access);
        return;
      }

      // Fallback if session expired between App auth check and paywall load
      const billingResponse = await fetch(`${url}/billing/access`, {
        method: 'GET',
        credentials: 'include',
      });
      if (billingResponse.ok) {
        const billingData = await billingResponse.json();
        setAccess(billingData.access ?? ANONYMOUS_ACCESS);
      } else {
        setAccess(ANONYMOUS_ACCESS);
      }
    } catch (error) {
      console.error('Failed to load access state:', error);
      setAccess(isAuthenticated ? {
        tier: 'active',
        canAccessAll: true,
        canJoinClass: true,
        canUseRememberMe: true,
        trialDaysRemaining: null,
        accessDaysRemaining: null,
        showJoinModalOnComplete: false,
        showRenewInNavbar: false,
        isGrandfathered: true,
      } : ANONYMOUS_ACCESS);
    } finally {
      setAccessLoading(false);
    }
  }, [authChecked, isAuthenticated]);

  useEffect(() => {
    if (!authChecked) {
      return;
    }
    if (!isAuthenticated) {
      setAccess(ANONYMOUS_ACCESS);
      setAccessLoading(false);
      return;
    }
    if (initialAccess) {
      setAccess(initialAccess);
      setAccessLoading(false);
      return;
    }
    refreshAccess();
  }, [authChecked, isAuthenticated, initialAccess, refreshAccess]);

  const openSignInModal = useCallback((context = null) => {
    setSignInModalContext(context);
    setSignInModalOpen(true);
  }, []);

  const closeSignInModal = useCallback(() => {
    setSignInModalOpen(false);
    setSignInModalContext(null);
  }, []);

  const openJoinModal = useCallback(() => {
    setJoinModalOpen(true);
  }, []);

  const closeJoinModal = useCallback(() => {
    setJoinModalOpen(false);
  }, []);

  const value = useMemo(() => ({
    access,
    accessLoading,
    refreshAccess,
    signInModalOpen,
    signInModalContext,
    openSignInModal,
    closeSignInModal,
    joinModalOpen,
    openJoinModal,
    closeJoinModal,
  }), [
    access,
    accessLoading,
    refreshAccess,
    signInModalOpen,
    signInModalContext,
    openSignInModal,
    closeSignInModal,
    joinModalOpen,
    openJoinModal,
    closeJoinModal,
  ]);

  return (
    <PaywallContext.Provider value={value}>
      {children}
    </PaywallContext.Provider>
  );
}

export function usePaywall() {
  const context = useContext(PaywallContext);
  if (!context) {
    throw new Error('usePaywall must be used within PaywallProvider');
  }
  return context;
}

export function usePaywallOptional() {
  return useContext(PaywallContext);
}
